import { FieldTypeEnum } from '@models/module/fields/enums';
import { ScreenFieldModel } from '@models/module/fields/main';

const DATA_CARD_GRID_ROW_HEIGHT = 24;
const DATA_CARD_GRID_MARGIN = 4;

/** The grid metrics as CSS lengths, for the host binding that publishes them to SCSS. */
export const DATA_CARD_GRID_CSS = {
  rowHeight: `${DATA_CARD_GRID_ROW_HEIGHT}px`,
  gap: `${DATA_CARD_GRID_MARGIN}px`,
} as const;

const DATA_CARD_FRAME = {
  margin: 5,
  border: 1,
  padding: 10,
  /** `app-data-card-view` pulls itself out by this much on every side. */
  hostOffset: 4,
} as const;

/** The same metrics as CSS lengths, for the host bindings that publish them to SCSS. */
export const DATA_CARD_FRAME_CSS = {
  margin: `${DATA_CARD_FRAME.margin}px`,
  border: `${DATA_CARD_FRAME.border}px`,
  padding: `${DATA_CARD_FRAME.padding}px`,
  hostOffset: `${DATA_CARD_FRAME.hostOffset}px`,
  negativeHostOffset: `-${DATA_CARD_FRAME.hostOffset}px`,
} as const;

/** Vertical space the frame adds around a card's grid. */
const DATA_CARD_FRAME_HEIGHT =
  2 * (DATA_CARD_FRAME.margin + DATA_CARD_FRAME.border + DATA_CARD_FRAME.padding - DATA_CARD_FRAME.hostOffset);

export const DATA_CARD_SINGLE_COLUMN_MAX_WIDTH = 724;
export const DATA_CARD_SKELETON_FALLBACK_HEIGHT = 340;

/** Table/Weather values render as multi-row tables; a single 24px row would crop them. */
function isTableLikeFieldType(typeId: FieldTypeEnum): boolean {
  return typeId === FieldTypeEnum.Table || typeId === FieldTypeEnum.Weather;
}

/** Attachment/Relation values render a variable-length list of chips/links; a single narrow
 *  1x1 cell crops them just as badly, though they don't need the full row width Table/Weather do. */
function isListFieldType(typeId: FieldTypeEnum): boolean {
  return typeId === FieldTypeEnum.Attachment || typeId === FieldTypeEnum.Relation;
}

/** Smallest a Table/Weather cell can shrink to (roughly a header + one data row). */
export const DATA_CARD_TABLE_MIN_ROWS = 3;
/** Largest a Table/Weather cell grows to before it scrolls internally instead. */
export const DATA_CARD_TABLE_MAX_ROWS = 8;

/** Smallest an Attachment/Relation cell can shrink to (enough for one item to render fully). */
export const DATA_CARD_LIST_MIN_ROWS = 2;
/** Largest an Attachment/Relation cell grows to before it scrolls internally instead. */
export const DATA_CARD_LIST_MAX_ROWS = 5;

/** Initial size when a Table/Weather/Attachment/Relation field is added in the settings editor;
 *  the user can resize it. */
export function getDataCardFieldDefaultRows(typeId: FieldTypeEnum): number {
  if (isTableLikeFieldType(typeId)) {
    return DATA_CARD_TABLE_MIN_ROWS;
  }
  return isListFieldType(typeId) ? DATA_CARD_LIST_MIN_ROWS : 1;
}

/** Worst-case row reservation for schema-only contexts (e.g. virtual list row height), where the
 *  actual content isn't known yet. */
export function getDataCardFieldMinRows(typeId: FieldTypeEnum): number {
  if (isTableLikeFieldType(typeId)) {
    return DATA_CARD_TABLE_MAX_ROWS;
  }
  return isListFieldType(typeId) ? DATA_CARD_LIST_MAX_ROWS : 1;
}

export function getDataCardFieldMinCols(typeId: FieldTypeEnum): number {
  if (isTableLikeFieldType(typeId)) {
    return 12;
  }
  return isListFieldType(typeId) ? 6 : 1;
}

/** Only Table/Weather are forced to start at column 0 and span the full grid width; Attachment/
 *  Relation just get a wider minimum (via `getDataCardFieldMinCols`) but can still sit side by side
 *  with other fields. */
export function isDataCardFieldFullWidth(typeId: FieldTypeEnum): boolean {
  return isTableLikeFieldType(typeId);
}

export function getDataCardGridRows(fields: ScreenFieldModel[]): number {
  return fields.reduce((maxRows, field) => {
    const row = field.y ?? 0;
    const rowSpan = Math.max(field.rows || 1, getDataCardFieldMinRows(field.typeId));
    return Math.max(maxRows, row + rowSpan);
  }, 1);
}

export function getDataCardGridColumns(fields: ScreenFieldModel[]): number {
  return fields.reduce((maxColumns, field) => {
    const column = field.x ?? 0;
    const columnSpan = Math.max(field.cols || 1, getDataCardFieldMinCols(field.typeId));
    return Math.max(maxColumns, column + columnSpan);
  }, 1);
}

/** Same as `getDataCardGridRows`/`getDataCardGridColumns`, but for parts whose row span already
 *  reflects actual content (`DataCardViewPart.effectiveRows`) instead of the field's schema size. */
export function getDataCardPartsGridRows(parts: { y: number; rows: number }[]): number {
  return parts.reduce((maxRows, part) => Math.max(maxRows, (part.y ?? 0) + (part.rows || 1)), 1);
}

export function getDataCardPartsGridColumns(parts: { x: number; cols: number }[]): number {
  return parts.reduce((maxColumns, part) => Math.max(maxColumns, (part.x ?? 0) + (part.cols || 1)), 1);
}

export function getDataCardPartsGridHeight(parts: { y: number; rows: number }[]): number {
  const gridRows = getDataCardPartsGridRows(parts);
  return gridRows * (DATA_CARD_GRID_ROW_HEIGHT + DATA_CARD_GRID_MARGIN) + DATA_CARD_GRID_MARGIN;
}

/** Structural shape only, so this doesn't need to import `DataCardViewPart` (would create a cycle). */
export interface DataCardReflowItem {
  x: number;
  y: number;
  cols: number;
  field: { rows?: number };
}

function columnsOverlap(a: DataCardReflowItem, b: DataCardReflowItem): boolean {
  const aStart = a.x ?? 0;
  const aEnd = aStart + (a.cols || 1);
  const bStart = b.x ?? 0;
  const bEnd = bStart + (b.cols || 1);
  return aStart < bEnd && bStart < aEnd;
}

/**
 * Shifts every part sitting at or below a Table/Weather/Attachment/Relation part by however much
 * its actual (content-based) size differs from its persisted row span — down if it grew (e.g. a
 * big table), up if it shrank (e.g. an empty one collapsing to a single row) — so parts below
 * never overlap or leave a gap. Only parts whose column range overlaps the changed part are
 * shifted; parts sitting side by side in a different column must not move.
 */
export function reflowDataCardParts<T extends DataCardReflowItem>(parts: T[], getRows: (part: T) => number): void {
  const changedParts = parts
    .map(part => ({ part, rows: getRows(part) }))
    .filter(({ part, rows }) => rows !== (part.field.rows || 1))
    .sort((a, b) => a.part.y - b.part.y);

  for (const { part: changed, rows: changedRows } of changedParts) {
    const delta = changedRows - (changed.field.rows || 1);
    if (delta === 0) {
      continue;
    }
    for (const part of parts) {
      if (part !== changed && part.y >= changed.y && columnsOverlap(changed, part)) {
        part.y += delta;
      }
    }
  }
}

export function getDataCardGridHeight(fields: ScreenFieldModel[]): number {
  const gridRows = getDataCardGridRows(fields);
  return gridRows * (DATA_CARD_GRID_ROW_HEIGHT + DATA_CARD_GRID_MARGIN) + DATA_CARD_GRID_MARGIN;
}

export function getDataCardVirtualRowHeight(fields: ScreenFieldModel[]): number {
  return getDataCardGridHeight(fields) + DATA_CARD_FRAME_HEIGHT;
}
